import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Projeto, useAtualizarProjetoDados } from "@/hooks/useProjetos";
import { toast } from "sonner";
import { useEffect } from "react";
import { useEmpresasResponsaveis, type EmpresaResponsavel } from "@/hooks/useEmpresasResponsaveis";
import { EmpresaSearchSelect } from "@/components/EmpresaSearchSelect";

interface EditarProjetoDialogProps {
  projeto: Projeto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditarProjetoDialog({ projeto, open, onOpenChange }: EditarProjetoDialogProps) {
  const { data: empresas = [] } = useEmpresasResponsaveis();
  const empresaInputRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState({
    valor_projeto: projeto.valor_projeto ? Number(projeto.valor_projeto) : 0,
    departamento: projeto.departamento || "vendas",
    observacoes: projeto.observacoes || "",
    empresa_responsavel_id: projeto.empresa_responsavel_id ?? null,
    empresa_responsavel: projeto.empresa_responsavel || "",
    cnpj_empresa: projeto.cnpj_empresa || "",
    telefone_empresa: projeto.telefone_empresa || "",
    email_empresa: projeto.email_empresa || "",
    is_projeto_elevo: projeto.is_projeto_elevo || false,
    id_projeto_elevo: projeto.id_projeto_elevo || "",
    empresa_projeto_externo_id: projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo || "",
    consultor_nome: projeto.consultor_nome || "",
    consultor_celular: projeto.consultor_celular || "",
    consultor_cpf_cnpj: projeto.consultor_cpf_cnpj || "",
    consultor_email: projeto.consultor_email || "",
    instalador_nome: projeto.instalador_nome || "",
    instalador_cpf_cnpj: projeto.instalador_cpf_cnpj || "",
    instalador_contato: projeto.instalador_contato || "",
    vendedor_nome: projeto.vendedor_nome || "",
    vendedor_celular: projeto.vendedor_celular || "",
    vendedor_cpf_cnpj: projeto.vendedor_cpf_cnpj || "",
  });

  const atualizarProjeto = useAtualizarProjetoDados();

  useEffect(() => {
    if (open) {
      setFormData({
        valor_projeto: projeto.valor_projeto ? Number(projeto.valor_projeto) : 0,
        departamento: projeto.departamento || "vendas",
        observacoes: projeto.observacoes || "",
        empresa_responsavel_id: projeto.empresa_responsavel_id ?? null,
        empresa_responsavel: projeto.empresa_responsavel || "",
        cnpj_empresa: projeto.cnpj_empresa || "",
        telefone_empresa: projeto.telefone_empresa || "",
        email_empresa: projeto.email_empresa || "",
        is_projeto_elevo: projeto.is_projeto_elevo || false,
        id_projeto_elevo: projeto.id_projeto_elevo || "",
        empresa_projeto_externo_id: projeto.empresa_projeto_externo_id || projeto.id_projeto_elevo || "",
        consultor_nome: projeto.consultor_nome || "",
        consultor_celular: projeto.consultor_celular || "",
        consultor_cpf_cnpj: projeto.consultor_cpf_cnpj || "",
        consultor_email: projeto.consultor_email || "",
        instalador_nome: projeto.instalador_nome || "",
        instalador_cpf_cnpj: projeto.instalador_cpf_cnpj || "",
        instalador_contato: projeto.instalador_contato || "",
        vendedor_nome: projeto.vendedor_nome || "",
        vendedor_celular: projeto.vendedor_celular || "",
        vendedor_cpf_cnpj: projeto.vendedor_cpf_cnpj || "",
      });
    }
  }, [open, projeto]);

  const isEmpresaElevo = (empresaNome?: string | null) => {
    const n = (empresaNome || '').toLowerCase();
    return n.includes('elevo');
  };

  const aplicarEmpresaDoCadastro = (empresa: EmpresaResponsavel) => {
    const elevo = isEmpresaElevo(empresa.nome);
    setFormData(prev => ({
      ...prev,
      empresa_responsavel_id: empresa.id,
      empresa_responsavel: empresa.nome || "",
      cnpj_empresa: empresa.cnpj || "",
      telefone_empresa: empresa.telefone || "",
      email_empresa: empresa.email || "",
      is_projeto_elevo: elevo,
      id_projeto_elevo: elevo ? (prev.empresa_projeto_externo_id || prev.id_projeto_elevo || "") : "",
    }));
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await atualizarProjeto.mutateAsync({
        projetoId: projeto.id,
        dados: {
          valor_projeto: Number(formData.valor_projeto) || 0,
          departamento: formData.departamento,
          observacoes: formData.observacoes,
          empresa_responsavel_id: formData.empresa_responsavel_id,
          empresa_projeto_externo_id: formData.empresa_projeto_externo_id || null,
          empresa_responsavel: formData.empresa_responsavel,
          cnpj_empresa: formData.cnpj_empresa,
          telefone_empresa: formData.telefone_empresa,
          email_empresa: formData.email_empresa,
          is_projeto_elevo: formData.is_projeto_elevo,
          id_projeto_elevo: formData.is_projeto_elevo ? (formData.empresa_projeto_externo_id || formData.id_projeto_elevo) : "",
          consultor_nome: formData.consultor_nome,
          consultor_celular: formData.consultor_celular,
          consultor_cpf_cnpj: formData.consultor_cpf_cnpj,
          consultor_email: formData.consultor_email,
          instalador_nome: formData.instalador_nome,
          instalador_cpf_cnpj: formData.instalador_cpf_cnpj,
          instalador_contato: formData.instalador_contato,
          vendedor_nome: formData.vendedor_nome,
          vendedor_celular: formData.vendedor_celular,
          vendedor_cpf_cnpj: formData.vendedor_cpf_cnpj,
        },
      });

      toast.success("Projeto atualizado com sucesso!");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao atualizar projeto:", error);
      toast.error("Erro ao atualizar projeto: " + error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Projeto #{projeto.id}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="departamento">Departamento</Label>
              <Input
                id="departamento"
                value={formData.departamento}
                onChange={(e) => handleChange("departamento", e.target.value)}
                placeholder="vendas, financeiro, tecnico, administrativo"
              />
            </div>
            <div>
              <Label htmlFor="valor_projeto">Valor do Projeto (R$)</Label>
              <Input
                id="valor_projeto"
                type="number"
                step="0.01"
                value={formData.valor_projeto}
                onChange={(e) => handleChange("valor_projeto", e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="is_projeto_elevo"
                checked={formData.is_projeto_elevo}
                onCheckedChange={(value) => handleChange("is_projeto_elevo", value)}
              />
              <div>
                <Label htmlFor="is_projeto_elevo">Projeto Elevo?</Label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => handleChange("observacoes", e.target.value)}
              rows={4}
              placeholder="Observações adicionais sobre o projeto..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-end justify-between gap-3">
                <div className="flex-1">
                  <EmpresaSearchSelect
                    ref={empresaInputRef}
                    value={formData.empresa_responsavel_id?.toString() || ""}
                    onChange={(empresaId, empresa) => {
                      const id = empresaId ? parseInt(empresaId) : null;
                      handleChange("empresa_responsavel_id", id);
                      if (empresa) aplicarEmpresaDoCadastro(empresa);
                    }}
                    label="Empresa (cadastro)"
                  />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    empresaInputRef.current?.focus();
                    const empresa = empresas.find(e => e.id === formData.empresa_responsavel_id);
                    if (empresa) {
                      aplicarEmpresaDoCadastro(empresa);
                      toast.success("Dados recarregados do cadastro da empresa!");
                    }
                  }}
                  disabled={!formData.empresa_responsavel_id}
                >
                  Recarregar do cadastro
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="empresa_responsavel">Empresa Responsável</Label>
              <Input
                id="empresa_responsavel"
                value={formData.empresa_responsavel}
                onChange={(e) => handleChange("empresa_responsavel", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cnpj_empresa">CNPJ da Empresa</Label>
              <Input
                id="cnpj_empresa"
                value={formData.cnpj_empresa}
                onChange={(e) => handleChange("cnpj_empresa", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="telefone_empresa">Telefone da Empresa</Label>
              <Input
                id="telefone_empresa"
                value={formData.telefone_empresa}
                onChange={(e) => handleChange("telefone_empresa", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="email_empresa">Email da Empresa</Label>
              <Input
                id="email_empresa"
                type="email"
                value={formData.email_empresa}
                onChange={(e) => handleChange("email_empresa", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="empresa_projeto_externo_id">ID do projeto na empresa</Label>
              <Input
                id="empresa_projeto_externo_id"
                value={formData.empresa_projeto_externo_id}
                onChange={(e) => handleChange("empresa_projeto_externo_id", e.target.value.replace(/\D/g, ''))}
                disabled={!formData.empresa_responsavel_id && !formData.empresa_responsavel}
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Consultor Responsável</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="consultor_nome">Nome</Label>
                <Input
                  id="consultor_nome"
                  value={formData.consultor_nome}
                  onChange={(e) => handleChange("consultor_nome", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="consultor_celular">Celular</Label>
                <Input
                  id="consultor_celular"
                  value={formData.consultor_celular}
                  onChange={(e) => handleChange("consultor_celular", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="consultor_cpf_cnpj">CPF/CNPJ</Label>
                <Input
                  id="consultor_cpf_cnpj"
                  value={formData.consultor_cpf_cnpj}
                  onChange={(e) => handleChange("consultor_cpf_cnpj", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="consultor_email">Email</Label>
                <Input
                  id="consultor_email"
                  value={formData.consultor_email}
                  onChange={(e) => handleChange("consultor_email", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Instalador</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="instalador_nome">Nome</Label>
                <Input
                  id="instalador_nome"
                  value={formData.instalador_nome}
                  onChange={(e) => handleChange("instalador_nome", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="instalador_contato">Contato</Label>
                <Input
                  id="instalador_contato"
                  value={formData.instalador_contato}
                  onChange={(e) => handleChange("instalador_contato", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="instalador_cpf_cnpj">CPF/CNPJ</Label>
                <Input
                  id="instalador_cpf_cnpj"
                  value={formData.instalador_cpf_cnpj}
                  onChange={(e) => handleChange("instalador_cpf_cnpj", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Vendedor</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="vendedor_nome">Nome</Label>
                <Input
                  id="vendedor_nome"
                  value={formData.vendedor_nome}
                  onChange={(e) => handleChange("vendedor_nome", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vendedor_celular">Celular</Label>
                <Input
                  id="vendedor_celular"
                  value={formData.vendedor_celular}
                  onChange={(e) => handleChange("vendedor_celular", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vendedor_cpf_cnpj">CPF/CNPJ</Label>
                <Input
                  id="vendedor_cpf_cnpj"
                  value={formData.vendedor_cpf_cnpj}
                  onChange={(e) => handleChange("vendedor_cpf_cnpj", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {atualizarProjeto.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

